import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Search as SearchIcon, Baby, ShieldCheck } from "lucide-react";
import Paywall from "../components/Paywall.jsx";
import { useIsPremium } from "../lib/premium";

export default function PaediatricDrugs() {
  const premium = useIsPremium();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [weight, setWeight] = useState("");

  useEffect(() => {
    if (!premium) return;
    api.get("/paediatric-drugs", { params: q ? { q } : {} }).then((r) => setItems(r.data)).catch(() => {});
  }, [q, premium]);

  const w = parseFloat(weight);
  const fmtNum = (n) => {
    const r = Math.round(n * 100) / 100;
    return Number.isInteger(r) ? String(r) : r.toFixed(2);
  };
  // Compute a calculated dose string for any object carrying dosePerKg fields.
  const calcDose = (d) => {
    if (!w || !d.dosePerKg) return null;
    let total = w * d.dosePerKg;
    if (d.maxSingleDose && total > d.maxSingleDose) total = d.maxSingleDose;
    let low = total;
    if (d.dosePerKgHigh) {
      let high = w * d.dosePerKgHigh;
      if (d.maxSingleDose && high > d.maxSingleDose) high = d.maxSingleDose;
      return `${fmtNum(low)}–${fmtNum(high)} ${d.doseUnit || "mg"}`;
    }
    return `${fmtNum(total)} ${d.doseUnit || "mg"}`;
  };
  const perKgLabel = (d) => {
    if (!d.dosePerKg) return null;
    const base = d.dosePerKgHigh ? `${d.dosePerKg}–${d.dosePerKgHigh}` : `${d.dosePerKg}`;
    return `${base} ${d.doseUnit}/kg`;
  };

  if (!premium) return <Paywall feature="Paediatric Dosing" />;

  return (
    <div className="space-y-6" data-testid="paed-drugs-page">
      <div>
        <div className="text-xs tracking-widest uppercase text-slate-500 font-semibold">Paediatrics</div>
        <div className="flex items-center justify-between flex-wrap gap-2 mt-1">
          <h1 className="font-heading text-3xl font-bold text-[#111827] flex items-center gap-2">
            <Baby className="w-7 h-7 text-[#111827]" /> Paediatric Drug Doses
          </h1>
          <Badge className="bg-slate-100 text-slate-700 border-0">{items.length} of 111</Badge>
        </div>
        <p className="text-slate-600 text-sm mt-1">Weight-based paediatric dosing per NDoH STG. Enter weight to auto-calculate.</p>
      </div>

      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search paediatric drug or indication..." className="pl-9 h-11 bg-white" data-testid="paed-search" />
        </div>
        <div className="flex items-center gap-2">
          <Label htmlFor="weight" className="text-sm whitespace-nowrap">Child weight (kg)</Label>
          <Input id="weight" type="number" value={weight} onChange={(e) => setWeight(e.target.value)} className="w-28 h-11 bg-white" placeholder="e.g. 12" data-testid="paed-weight" />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        {items.map((d) => {
          const hasRoutes = Array.isArray(d.routeDoses) && d.routeDoses.length > 0;
          const calc = calcDose(d);
          return (
            <Card key={d.id} className="border-slate-200" data-testid={`paed-card-${d.id}`}>
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <Badge className="bg-[#EFF4FF] text-[#111827] border-0 text-[10px]">{d.category}</Badge>
                  {!hasRoutes && d.route && <span className="text-xs text-slate-500">{d.route}</span>}
                  {hasRoutes && (
                    <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px] flex items-center gap-1" data-testid={`paed-route-verified-${d.id}`}>
                      <ShieldCheck className="w-3 h-3" /> Route-specific dosing
                    </Badge>
                  )}
                </div>
                <h3 className="font-heading font-semibold text-[#111827]">{d.name}</h3>
                {d.indication && <p className="text-sm text-slate-600 mt-1">{d.indication}</p>}

                {hasRoutes ? (
                  <div className="mt-3 pt-3 border-t border-slate-100 space-y-2" data-testid={`paed-routes-${d.id}`}>
                    {d.routeDoses.map((rd, i) => {
                      const rc = calcDose(rd);
                      const pk = perKgLabel(rd);
                      return (
                        <div key={i} className="rounded-md border border-slate-200 p-2.5" data-testid={`paed-route-${d.id}-${rd.route}`}>
                          <div className="flex items-center justify-between gap-2">
                            <Badge className="bg-slate-900 text-white border-0 text-[10px] font-semibold tracking-wide">{rd.route}</Badge>
                            {pk && <span className="text-sm font-medium text-[#111827]">{pk}</span>}
                          </div>
                          <div className="mt-1 space-y-0.5 text-sm">
                            {rd.frequency && <div><span className="text-slate-500">Frequency: </span>{rd.frequency}</div>}
                            {rd.maxSingleDose && <div><span className="text-slate-500">Max: </span>{rd.maxSingleDose} {rd.maxSingleUnit || rd.doseUnit}</div>}
                            {rd.notes && <div className="text-xs text-slate-600">{rd.notes}</div>}
                            {rc && (
                              <div className="mt-1 p-2 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-900">
                                <span className="text-xs font-semibold">Dose for {w} kg ({rd.route}): </span>
                                <span className="font-bold">{rc}</span>
                              </div>
                            )}
                            {!rd.dosePerKg && rd.notes == null && (
                              <div className="text-xs text-slate-500">Not weight-based — see notes.</div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {d.notes && <div className="text-xs text-slate-600 pt-1">{d.notes}</div>}
                    {d.stgRef && <div className="text-[10px] text-slate-400">{d.stgRef}</div>}
                  </div>
                ) : (
                  <div className="mt-3 pt-3 border-t border-slate-100 space-y-1 text-sm">
                    <div><span className="text-slate-500">Dose per kg: </span><span className="font-medium">{d.dosePerKg} {d.doseUnit}</span></div>
                    {d.frequency && <div><span className="text-slate-500">Frequency: </span>{d.frequency}</div>}
                    {d.maxSingleDose && <div><span className="text-slate-500">Max: </span>{d.maxSingleDose} {d.maxSingleUnit}</div>}
                    {d.formulation && <div><span className="text-slate-500">Formulation: </span>{d.formulation}</div>}
                    {calc && (
                      <div className="mt-2 p-2 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-900">
                        <span className="text-xs font-semibold">Calculated dose for {w} kg: </span>
                        <span className="font-bold">{calc}</span>
                      </div>
                    )}
                    {d.notes && <div className="text-xs text-slate-600 mt-2">{d.notes}</div>}
                    {d.stgRef && <div className="text-[10px] text-slate-400 mt-1">{d.stgRef}</div>}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
      {items.length === 0 && <div className="text-center py-20 text-slate-500 text-sm">No drugs found.</div>}
    </div>
  );
}
